/**
 * Layout lifted off the official Form-11 PDF (ভোটার তালিকায় নতুন নাম
 * অন্তর্ভুক্তির আবেদন, with its ক্ষমতাপত্র and অঙ্গীকারনামা).
 *
 * That file is real vector text, not a scan, so nothing here was eyeballed:
 * every baseline, run start and dotted-leader span comes straight out of its
 * text matrix. A leader is a run of full stops at a fixed pitch (3.095 pt at the
 * form's 13 pt face, 3.327 pt at the 14 pt declaration), so its length is
 * `dots × pitch` from where the run starts, and a label's width is the run's
 * width less its dots. Where one run mixes words and several leaders, the word
 * widths were set in Kalpurush and scaled so the run still ends where the
 * original ends.
 *
 * Coordinates are in the original's own US-Letter space (612 × 792 pt, y down
 * from the top, baselines). The page here is A4, so `X_SHIFT` re-centres them.
 */

/** Page box — A4 at 72 dpi, the paper these forms are actually printed on. */
export const PAGE_WIDTH = 595;
export const PAGE_HEIGHT = 842;

/** Letter → A4 width difference, split evenly so the margins stay level. */
export const X_SHIFT = -8.5;

/**
 * Kalpurush sets ~1.36× wider than the form's SutonnyMJ at the same size, so the
 * face is solved per line, not copied: 13 pt SutonnyMJ ≈ 11.1 px, the 14 pt
 * declaration ≈ 12 px, and the 27 pt title ≈ 23.3 px (median of the measured
 * line widths, all within ±3 %).
 */
export const TYPE = {
    body: { size: 11.1, weight: 400 },
    decl: { size: 12, weight: 400 },
    commission: { size: 15.4, weight: 740 },
    declTitle: { size: 23.3, weight: 740 },
};

/** Text sits in a 1.5 em line box, so dots and descenders stay inside the clip. */
export const LINE_BOX = 1.5;

/** Kalpurush: ascent 1 em, descent 0.4 em → baseline 1.05 em into a 1.5 em box. */
export const BASELINE_RATIO = (LINE_BOX - 1.4) / 2 + 1;

/** Dot pitch of the printed leaders, per face. */
export const PITCH = { body: 3.095, decl: 3.327 };

/**
 * Leaders are real full stops in the form's own font. Kalpurush's period
 * advances 0.25 em, so each run takes a little tracking to reach the pitch.
 */
export const LEADER = {
    char: '.',
    count: 240,
    letterSpacing: (size, pitch) => pitch - size * 0.25,
};

/**
 * Typed answers are not part of the printed form, so they print larger than the
 * labels to stay legible on a photocopy, and drop toward `minSize` only when the
 * blank is too short for the answer.
 */
export const VALUE_TYPE = { size: 13.2, minSize: 9.6, weight: 400 };

/** Values sit above their dots the way handwriting does. */
export const VALUE_LIFT = 3.5;
export const VALUE_INSET = 6;

/** The Election Commission emblem, as placed on page 1. */
export const EMBLEM = { left: 63.3, top: 42, width: 40, height: 40 };

/** The four name boxes on page 1 (x, y, w, h — the rectangle stroke is 0.72). */
const NAME_BOX_X = 143.3;
const NAME_BOX_W = 390;

const text = (t, y, x, extra = {}) => ({ k: 'text', t, y, x, ...extra });
const center = (t, y, extra = {}) => ({ k: 'text', t, y, center: 310, ...extra });
const leader = (y, from, to, extra = {}) => ({ k: 'leader', y, from, to, ...extra });
const value = (field, y, from, to, extra = {}) => ({ k: 'value', field, y, from, to, ...extra });
const decl = { size: TYPE.decl.size, pitch: PITCH.decl };

/**
 * Page 1 — Form-11. `box` rows print their answer inside a drawn rectangle;
 * `value` items with no leader are the lines the form leaves open after a label.
 */
export const PAGE1 = [
    { k: 'emblem' },
    center('বাংলাদেশ নির্বাচন কমিশন', 82.0, { size: TYPE.commission.size, weight: TYPE.commission.weight }),
    center('ফরম -১১', 99.3, { weight: 700 }),
    center('[বিধি ৩২ (২ ) দ্রষ্টব্য]', 115.0, { weight: 700 }),
    center('ভোটার তালিকায় নতুন নাম অন্তর্ভুক্তির আবেদন', 130.6, { weight: 700 }),
    text('প্রাপক,', 146.2, 57.6, { weight: 700 }),
    text('রেজিস্ট্রেশন অফিসার', 161.8, 57.6, { weight: 700 }),
    value('officeLine', 177.4, 57.6, 540, { lift: 0, inset: 0 }),

    { k: 'box', x: NAME_BOX_X, y: 198.5, w: NAME_BOX_W, h: 20.4 },
    { k: 'box', x: NAME_BOX_X, y: 241.9, w: NAME_BOX_W, h: 22.3 },
    { k: 'box', x: NAME_BOX_X, y: 288.6, w: NAME_BOX_W, h: 20.6 },
    { k: 'box', x: NAME_BOX_X, y: 336.7, w: NAME_BOX_W, h: 22.7 },
    text('১। দাবিদারের নাম :', 208.6, 57.6),
    value('applicantName', 208.6, 143.3, 533, { lift: 0 }),
    text('২। পিতার নাম :', 255.5, 57.6),
    value('fatherName', 255.5, 143.3, 533, { lift: 0 }),
    text('৩। মাতার নাম :', 302.3, 57.6),
    value('motherName', 302.3, 143.3, 533, { lift: 0 }),
    text('৪। স্বামী/স্ত্রীর নাম :', 349.3, 57.6),
    value('spouseName', 349.3, 143.3, 533, { lift: 0 }),

    text('৫। ঠিকানা:', 380.3, 57.6),
    text('গ্রাম:/মহল্লা', 398.2, 57.6),
    leader(398.2, 103.0, 523.9),
    value('village', 398.2, 103.0, 523.9),

    text('(ক) রাস্তার নাম ও নম্বর:', 416.2, 57.6),
    value('road', 416.2, 150.4, 540, { lift: 0 }),
    text('(খ) বাড়ী নম্বর ও ফোন (যদি থাকে)', 434.2, 57.6),
    value('houseNo', 434.2, 198.4, 540, { lift: 0 }),
    text('(গ) ডাকঘর:', 452.1, 57.6),
    value('postOffice', 452.1, 107.4, 540, { lift: 0 }),
    text('(ঘ) ওয়ার্ড:', 470.1, 57.6),
    value('ward', 470.1, 100.8, 540, { lift: 0 }),
    text('(ঙ) ইউনিয়ন/ পৌর এলাকা', 488.1, 57.6),
    value('union', 488.1, 165.2, 540, { lift: 0 }),
    text('(চ) থানা:', 506.0, 57.6),
    value('thana', 506.0, 98.0, 540, { lift: 0 }),
    text('(ছ) জেলা:', 524.0, 57.6),
    value('district', 524.0, 100.0, 540, { lift: 0 }),
    text('(জ) বিভাগ:', 542.0, 57.6),
    value('division', 542.0, 104.0, 540, { lift: 0 }),

    text('৬। ভোটার তালিকায় নাম অন্তর্ভুক্ত না হওয়ার কারণ:', 559.9, 57.6),
    value('notIncludedReason', 559.9, 257.1, 540, { lift: 0 }),

    text('৭। বর্তমান ঠিকানায়ঃ', 577.9, 57.6),
    leader(577.9, 141.0, 241.9),
    value('stayYears', 577.9, 141.0, 241.9),
    text('বৎসর', 577.9, 241.9),
    leader(577.9, 268.5, 318.0),
    value('stayMonths', 577.9, 268.5, 318.0),
    text('মাস বসবাস করিতেছি এবং আমার দাবীর সপক্ষে নিম্নোক্ত', 577.9, 321.0),
    text('প্রমাণাদি দাখিল করিলাম।', 595.9, 57.6),

    { k: 'tick', field: 'proofWatchman', x: 81.0, y: 623.2, w: 21.0, h: 15.0 },
    text('চৌকিদারের রশিদ', 631.8, 111.1),
    { k: 'tick', field: 'proofHoldingTax', x: 201.0, y: 621.6, w: 20.3, h: 15.0 },
    text('পৌরকর রশিদ', 631.8, 237.7),
    { k: 'tick', field: 'proofRent', x: 304.6, y: 621.6, w: 21.0, h: 15.0 },
    text('বাড়ী ভাড়ার রশিদ', 631.8, 334.4),
    { k: 'tick', field: 'proofUtility', x: 413.3, y: 621.6, w: 20.3, h: 15.0 },
    text('ইউটিলিটি বিল', 631.8, 437.5),
    { k: 'tick', field: 'proofOther', x: 497.3, y: 621.6, w: 22.4, h: 16.6 },
    text('অন্যান্য', 631.8, 525.7),

    text('৮। আমি এতদ্বারা ঘোষণা করিতেছি যে,', 649.7, 57.6),
    text('(ক) আমি বাংলাদেশের নাগরিক', 667.7, 57.6),
    text('(খ) আমি কোন উপযুক্ত আদালত কর্তৃক অপ্রকৃতিস্থ ঘোষিত হই নাই', 685.7, 57.6),

    text('(গ) ২০', 703.5, 57.6),
    leader(703.5, 91.9, 104.3),
    // Only two digits go in a four-dot blank, so they print at body size and may
    // run a hair past the dots (the next word starts at 107.4) rather than shrink.
    value('ageYearTail', 703.5, 91.9, 107.0, { inset: 0.5, size: 11.1, minSize: 11.1 }),
    text('সালের ১লা জানুয়ারী তারিখে', 703.5, 107.4),
    text('আমার বয়স', 703.5, 225.1),
    leader(703.5, 273.2, 344.4),
    value('ageYears', 703.5, 273.2, 344.4),
    text('বৎসর', 703.5, 347.4),
    leader(703.5, 374.5, 427.1),
    value('ageMonths', 703.5, 374.5, 427.1),
    text('মাস', 703.5, 430.1),
    leader(703.5, 445.3, 522.6),
    value('ageDays', 703.5, 445.3, 522.6),
    text('দিন', 703.5, 522.6),

    text('(বয়স প্রমাণের নিমিত্ত এস,এস,সি সার্টিফিকেট অথবা সংশ্লিষ্ট শিক্ষা প্রতিষ্ঠানের সনদ পত্র এর সত্যায়িত অনুলিপি বা জন্ম নিবন্ধন', 721.5, 57.6),
    text('সনদ পত্র বা চেয়ারম্যান/কমিশনারের প্রত্যয়নপত্র সংযুক্ত করিতে হইবে)', 739.5, 57.6),
];

/** Page 2 — the rest of the application, the ক্ষমতাপত্র and the office box. */
export const PAGE2 = [
    text('(ঘ)', 83.1, 57.6),
    text('আমি অন্য কোন ভোটার এলাকায় ভোটার তালিকায় নাম অন্তর্ভুক্তির জন্য আবেদন করি নাই কিংবা আমার নাম তালিকাভুক্ত হয়', 83.1, 80.1, { ls: -0.076 }),
    text('নাই', 101.1, 57.6),
    text('৯। অতএব, ৫ নম্বর ক্রমিকে বর্ণিত ঠিকানায় প্রস্তুতকৃত ভোটার তালিকায় আমার নাম অন্তর্ভুক্তির জন্য আবেদন করিতেছি এবং', 101.1, 75.6, { ls: 0.023 }),
    text('এই মর্মে', 119.1, 57.6),
    text('প্রত্যয়ন করিতেছি যে, আমার জ্ঞান ও বিশ্বাস মতে উপরে উল্লেখিত বিবরণ সত্য। এতদসঙ্গে নির্ধারিত ২ নম্বর ফরম পূর্বক সংযুক্ত', 136.9, 57.6, { ls: -0.012 }),
    text('করা হইল', 154.9, 57.6),
    text('তারিখ:', 172.9, 57.6),
    leader(172.9, 87.5, 174.2),
    value('applicationDate', 172.9, 87.5, 174.2),

    leader(208.8, 456.8, 524.9),
    text('আবেদনকারীর /স্বাক্ষর বা টিপসই', 226.7, 425.0),

    center('ক্ষমতাপত্র', 244.7),
    text('আমি আমার', 280.6, 57.6),
    text('পিতা/মাতা/স্বামী/স্ত্রী/ভগ্নী/পুত্র/কন্যা', 280.6, 107.3),
    leader(298.6, 57.6, 407.3),
    value('representativeName', 298.6, 57.6, 407.3),
    text('পিতা/ স্বামী', 316.6, 57.6),
    leader(316.6, 105.7, 251.2),
    value('representativeFather', 316.6, 105.7, 251.2),
    text('কে আমার পক্ষে এই আবেদন পেশ করার ক্ষমতা প্রদান করিতেছি ।', 316.6, 254.3),

    text('তারিখ:', 352.5, 57.6),
    leader(352.5, 87.6, 195.9),
    value('authorizationDate', 352.5, 87.6, 195.9),
    leader(370.5, 456.8, 524.9),
    text('আবেদনকারীর /স্বাক্ষর বা টিপসই', 388.4, 428.6),
    leader(424.4, 453.2, 527.6),
    text('প্রতিনিধির স্বাক্ষর বা টিপসই', 442.3, 428.6),

    text('ঠিকানা:', 496.1, 354.3),
    leader(496.1, 382.6, 562.1),
    value('representativeAddress', 496.1, 382.6, 562.1),

    text('(রেজিস্ট্রেশন অফিসারের কার্যালয় কর্তৃক পূরণ করিতে হইবে)', 532.0, 57.6),
    text('আবেদন নম্বর', 568.0, 61.1),
    leader(568.0, 116.4, 162.8),
    text('পেশের তারিখ', 585.9, 61.1),
    leader(585.9, 118.5, 164.9),
    text('শুনানীর তারিখ (প্রয়োজনবোধে)', 603.9, 61.1),
    leader(603.9, 184.2, 230.6),
    text('রেজিস্ট্রেশন অফিসারের সিদ্ধান্ত :', 621.9, 61.1),
    text('তারিখ :', 639.8, 61.1),
    leader(657.8, 467.4, 520.2),
    text('রেজিস্ট্রেশন অফিসারের স্বাক্ষর', 675.8, 435.6),
];

/** Page 3 — অঙ্গীকারনামা, 14 pt in the original. */
export const PAGE3 = [
    center('অঙ্গীকার নামা', 121.7, { size: TYPE.declTitle.size, weight: TYPE.declTitle.weight }),
    { k: 'rule', x: 255.3, y: 124.4, w: 109.5, h: 0.9 },

    text('আমি', 172.5, 107.2, decl),
    leader(172.5, 135.7, 352.0, decl),
    value('applicantName', 172.5, 135.7, 352.0),
    // The visarga is attached to the word: set alone after a space, Kalpurush
    // draws it on a dotted-circle placeholder.
    text(',পিতাঃ', 172.5, 352.0, decl),
    leader(172.5, 396.0, 552.8, decl),
    value('fatherName', 172.5, 396.0, 552.8),

    text('মাতাঃ', 205.2, 71.2, decl),
    leader(205.2, 95.1, 244.8, decl),
    value('motherName', 205.2, 95.1, 244.8),
    text(',গ্রামঃ', 205.2, 244.8, decl),
    leader(205.2, 268.4, 404.8, decl),
    value('village', 205.2, 268.4, 404.8),
    text('ডাকঘর', 205.2, 404.8, decl),
    leader(205.2, 435.5, 551.9, decl),
    value('postOffice', 205.2, 435.5, 551.9),

    { k: 'flow', y: 238.0, x: 71.2, width: 481.6 },

    text('বাংলাদেশী নাগরিক এবং “ভোটার তালিকা আইন-২০০৯” ও জাতীয় পরিচয় নিবন্ধন আইন-২০১০” অনুসারে ভোটার', 270.6, 71.2, { ...decl, ls: 0.016 }),
    text('তালিকায় নাম অন্তর্ভুক্তিসহ জাতীয় পরিচয়পত্র পাওয়ার যোগ্য। আমি ইতোপূর্বে কখনো বাংলাদেশের কোন ভোটার', 303.4, 71.2, { ...decl, ls: 0.285 }),
    text('এলাকার ভোটার তালিকায় নাম অন্তর্ভুক্ত করি নাই।', 336.2, 71.2, { ...decl, ls: -0.153 }),

    text('আমি আরও অঙ্গীকার করিতেছি যে, নির্বাচন কমিশনের ডাটাবেইজে আমার নামে দ্বৈত অন্তর্ভুক্তি পাওয়া গেলে', 385.3, 107.2, { ...decl, ls: -0.024 }),
    text('কিংবা আমি কোনরুপ মিথ্যা বা ভ্রান্ত তথ্য প্রদান করিয়া পরিচয় নিবন্ধন করিলে, ডাটাবেইজ হইতে নাম কর্তন ও জাতীয়', 418.0, 71.2, { ...decl, ls: -0.182 }),
    text('পরিচয়পত্র বাতিলসহ আমার বিরুদ্ধে যে আইনানুগ ব্যবস্থা গ্রহণ করা হইবে আমি তাহা মানিয়া লইতে বাধ্য থাকিব।', 450.7, 71.2, { ...decl, ls: -0.074 }),

    text('স্বাক্ষরঃ', 516.2, 309.6, decl),
    leader(516.2, 347.0, 530.7, decl),
    text('তারিখঃ', 549.0, 309.6, decl),
    leader(549.0, 346.0, 533.1, decl),
    value('applicationDate', 549.0, 346.0, 533.1),
    text('মোবাইলঃ', 581.6, 309.6, decl),
    leader(581.6, 357.0, 532.6, decl),
    value('mobile', 581.6, 357.0, 532.6),
];

/** The flowing paragraph on page 3 that carries the district values. */
export const FLOW_TAIL = 'এই মর্মে অঙ্গীকার করিতেছি যে, আমি উল্লেখিত ঠিকানায় বসবাসরত আঠারোর্ধ';
